// Copyright (c) Docugami, Inc. All rights reserved.

import { selectChunkThunk } from "hooks/chunkSelection";
import { Dispatch } from 'redux';
import type { AppDispatch, AppState } from 'store';
import { APPEND_DOCUMENT, APPEND_DOCUMENTS, CanvasSelectionMode, DocumentNumberPayload, DocumentPayload, DocumentsActionTypes, DocumentsPayload, DocumentState, ModalSelectionArea, ModalSelectionDisplayContext, ModalSelectionItemType, SET_DOCUMENT_PAGE_COUNT, SET_DOCUMENT_VIEWER_HEIGHT, SET_MODAL_DISPLAY_CONTEXT, SET_MODAL_SELECTION_CONTEXT, SET_SELECTION_MODE, SET_SELECTION_MODE_LOCKED, UPDATE_DOCUMENT } from "./types";
import { ClusterDocument, DocumentApi } from "networking/api";

export function appendDocuments(newState: DocumentsPayload): DocumentsActionTypes {
    return {
        type: APPEND_DOCUMENTS,
        payload: newState
    };
}

export function appendDocument(payload: DocumentState): DocumentsActionTypes {
    return {
        type: APPEND_DOCUMENT,
        payload: payload
    };
}

export function updateDocument(payload: DocumentPayload): DocumentsActionTypes {
    return {
        type: UPDATE_DOCUMENT,
        payload: payload
    };
}

export function setPageCount(payload: DocumentNumberPayload): DocumentsActionTypes {
    return {
        type: SET_DOCUMENT_PAGE_COUNT,
        payload: payload
    };
}

export function setSelectionMode(value: CanvasSelectionMode): DocumentsActionTypes {
    return {
        type: SET_SELECTION_MODE,
        payload: value
    }
}

export function setSelectionModeLocked(value: boolean): DocumentsActionTypes {
    return {
        type: SET_SELECTION_MODE_LOCKED,
        payload: value
    }
}

export function setDocumentViewerHeight(height: number): DocumentsActionTypes {
    return {
        type: SET_DOCUMENT_VIEWER_HEIGHT,
        payload: height
    }
}

export function ensureDocument(folderId: string): ((dispatch: AppDispatch, getState: (() => AppState)) => Promise<ClusterDocument | undefined>) {
    return async function (dispatch: AppDispatch, getState: (() => AppState)) {
        const state = getState();
        if (state.documents.allIds.includes(folderId)) {
            return state.documents.indexed[folderId].clusterDocument;
        }
        else {
            const document = (await new DocumentApi().documentGetDocumentsFromFolderIds([folderId])).data?.[0];
            if (!!document) {
                dispatch(appendDocument({ clusterDocument: document }));
                return document;
            }
        }
    }
}

export function ensureDocumentState(
    documentId: string): ((dispatch: Dispatch<any>, getState: (() => AppState)) => Promise<any | any[]>) {
    return function (dispatch: Dispatch<any>, getState: (() => AppState)) {
        const state = getState();
        if (!!documentId && documentId in state.documents.indexed) {
            dispatch(selectChunkThunk());
        }

        return Promise.resolve();
    }
}

export function enableModalSelection(itemType: ModalSelectionItemType, displayContext: ModalSelectionDisplayContext, autoConfirm?: boolean, areas?: ModalSelectionArea[], dropTarget?: boolean): DocumentsActionTypes {
    return {
        type: SET_MODAL_SELECTION_CONTEXT,
        payload: {
            isActive: true,
            itemType: itemType,
            autoConfirm: autoConfirm,
            areas: areas,
            displayContext: displayContext,
            dropTarget: dropTarget
        }
    }
}

export function disableModalSelection(): DocumentsActionTypes {
    return {
        type: SET_MODAL_SELECTION_CONTEXT,
        payload: {
            itemType: ModalSelectionItemType.None,
            isActive: false
        }
    }
}

export function setModalDisplayContext(displayContext: Partial<ModalSelectionDisplayContext>): DocumentsActionTypes {
    return {
        type: SET_MODAL_DISPLAY_CONTEXT,
        payload: displayContext
    }
}
